// TouchInput.ts — Phaser-based touch/pointer input handler

import Phaser from 'phaser'

export interface TouchInputCallbacks {
  onTap?: (x: number, y: number) => void
  onDragStart?: (x: number, y: number) => void
  onDragMove?: (x: number, y: number, dx: number, dy: number) => void
  onDragEnd?: (x: number, y: number) => void
  onPinchZoom?: (scaleDelta: number) => void
}

const TAP_MAX_MOVE_PX = 10
const TAP_MAX_DURATION_MS = 300
const ZOOM_MIN = 0.7
const ZOOM_MAX = 2.0

export class TouchInput {
  private scene: Phaser.Scene
  private callbacks: TouchInputCallbacks
  private enabled = true

  private dragStartX = 0
  private dragStartY = 0
  private dragStartTime = 0
  private isDragging = false

  private pinchStartDistance = 0
  private lastPinchDistance = 0
  private currentZoom = 1.0

  constructor(scene: Phaser.Scene, callbacks: TouchInputCallbacks) {
    this.scene = scene
    this.callbacks = callbacks
    this.register()
  }

  private register(): void {
    this.scene.input.on('pointerdown', this.onDown, this)
    this.scene.input.on('pointermove', this.onMove, this)
    this.scene.input.on('pointerup', this.onUp, this)
    // Multi-touch pinch listener is always registered; onMultiMove checks isDown internally
    this.scene.input.on('pointermove', this.onMultiMove, this)
  }

  disable(): void { this.enabled = false }
  enable(): void  { this.enabled = true  }

  private onDown(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return
    this.dragStartX = pointer.x
    this.dragStartY = pointer.y
    this.dragStartTime = pointer.downTime
    this.isDragging = false

    // Reset pinch tracking whenever a new touch starts
    const p1 = this.scene.input.pointer1
    const p2 = this.scene.input.pointer2
    if (p1.isDown && p2.isDown) {
      const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y)
      this.pinchStartDistance = dist
      this.lastPinchDistance = dist
    } else {
      this.pinchStartDistance = 0
      this.lastPinchDistance = 0
    }
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled || !pointer.isDown) return

    const dx = pointer.x - pointer.prevPosition.x
    const dy = pointer.y - pointer.prevPosition.y
    const totalMove = Math.hypot(pointer.x - this.dragStartX, pointer.y - this.dragStartY)

    if (totalMove > TAP_MAX_MOVE_PX) {
      if (!this.isDragging) {
        this.isDragging = true
        this.callbacks.onDragStart?.(this.dragStartX, this.dragStartY)
      }
      this.callbacks.onDragMove?.(pointer.x, pointer.y, dx, dy)
    }
  }

  private onUp(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) { this.isDragging = false; return }
    const elapsed = pointer.upTime - this.dragStartTime
    const totalMove = Math.hypot(pointer.x - this.dragStartX, pointer.y - this.dragStartY)

    if (this.isDragging) {
      this.callbacks.onDragEnd?.(pointer.x, pointer.y)
    } else if (elapsed < TAP_MAX_DURATION_MS && totalMove < TAP_MAX_MOVE_PX) {
      this.callbacks.onTap?.(pointer.x, pointer.y)
    }

    this.isDragging = false
    this.pinchStartDistance = 0
    this.lastPinchDistance = 0
  }

  private onMultiMove(_pointer: Phaser.Input.Pointer): void {
    const p1 = this.scene.input.pointer1
    const p2 = this.scene.input.pointer2
    if (!p1.isDown || !p2.isDown) {
      this.pinchStartDistance = 0
      this.lastPinchDistance = 0
      return
    }

    const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y)
    if (this.pinchStartDistance === 0) {
      this.pinchStartDistance = dist
      this.lastPinchDistance = dist
      return
    }

    const rawDelta = dist / this.lastPinchDistance
    this.lastPinchDistance = dist

    // Clamp zoom to [ZOOM_MIN, ZOOM_MAX] and derive a clamped delta
    const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.currentZoom * rawDelta))
    const clampedDelta = newZoom / this.currentZoom
    this.currentZoom = newZoom

    this.callbacks.onPinchZoom?.(clampedDelta)
  }

  destroy(): void {
    this.scene.input.off('pointerdown', this.onDown, this)
    this.scene.input.off('pointermove', this.onMove, this)
    this.scene.input.off('pointerup', this.onUp, this)
    this.scene.input.off('pointermove', this.onMultiMove, this)
  }
}
